import { Badge, Box, Text } from "@chakra-ui/react-v3";
import dayjs from "dayjs";
import { MedicationSeriesRowData, EnhancedMedicationSeries } from "../tables/medication";
import {
  ResourceColumnDef,
  ResourceTableDefinition,
  CellContext,
} from "../utils/resource-column-definitions";
import { HighlightedText } from "../components/highlighted-text";
import { BaseSeries } from "../table-transforms/series-utils";
import { SidePanelData } from "../types/side-panel-data";

export const MEDICATION_RESOURCE_TYPE = "Medication";

/**
 * Column definitions for Medication resources using TanStack Table.
 * This configuration provides sorting, custom rendering, and enhanced UX for medication data.
 */
export const medicationColumns: ResourceColumnDef<MedicationSeriesRowData>[] = [
  {
    accessorKey: "medication",
    header: "Medication Name",
    enableSorting: true,
    cell: info => {
      const value = info.getValue() as string;
      const filterQuery = info.table.options.meta?.filterQuery || "";

      return (
        <Box fontWeight="medium">
          <HighlightedText text={value} searchQuery={filterQuery} />
        </Box>
      );
    },
  },
  {
    accessorKey: "dose",
    header: "Dose",
    enableSorting: true,
    cell: (info: CellContext<MedicationSeriesRowData>) => {
      const value = info.getValue() as string;
      const filterQuery = info.table.options.meta?.filterQuery || "";

      // Highlight non-empty doses
      if (value && value !== "-") {
        return (
          <Text color="status.info" fontWeight="medium">
            <HighlightedText text={value} searchQuery={filterQuery} />
          </Text>
        );
      }

      return <HighlightedText text={value} searchQuery={filterQuery} />;
    },
  },
  {
    accessorKey: "quantity",
    header: "Quantity",
    enableSorting: true,
    cell: (info: CellContext<MedicationSeriesRowData>) => {
      const value = info.getValue() as string;
      const filterQuery = info.table.options.meta?.filterQuery || "";

      return <HighlightedText text={value} searchQuery={filterQuery} />;
    },
  },
  {
    accessorKey: "lastFillDate",
    header: "Last Fill Date",
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      const dateA = rowA.getValue<string>("lastFillDate");
      const dateB = rowB.getValue<string>("lastFillDate");

      if (dateA === "-" && dateB === "-") return 0;
      if (dateA === "-") return 1;
      if (dateB === "-") return -1;

      return dayjs(dateA).diff(dayjs(dateB));
    },
    cell: (info: CellContext<MedicationSeriesRowData>) => {
      const value = info.getValue() as string;
      const filterQuery = info.table.options.meta?.filterQuery || "";

      // Highlight recent dates (within last 30 days)
      if (value && value !== "-") {
        const isRecent = dayjs().diff(dayjs(value), "days") <= 30;

        if (isRecent) {
          return (
            <Badge colorPalette="green" size="sm">
              <HighlightedText text={value} searchQuery={filterQuery} />
            </Badge>
          );
        }
      }

      return <HighlightedText text={value} searchQuery={filterQuery} />;
    },
  },
  {
    accessorKey: "date",
    header: "Date",
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      const dateA = rowA.getValue<string>("date");
      const dateB = rowB.getValue<string>("date");

      if (dateA === "-" && dateB === "-") return 0;
      if (dateA === "-") return 1;
      if (dateB === "-") return -1;

      return dayjs(dateA).diff(dayjs(dateB));
    },
    cell: (info: CellContext<MedicationSeriesRowData>) => {
      const value = info.getValue() as string;
      const filterQuery = info.table.options.meta?.filterQuery || "";

      return <HighlightedText text={value} searchQuery={filterQuery} />;
    },
  },
];

/**
 * Complete table definition for Medication resources.
 * Includes columns, initial sorting, and custom behavior.
 */
export const medicationTableDefinition: ResourceTableDefinition<MedicationSeriesRowData> = {
  columns: medicationColumns,

  // Sort by most recent medications first (by start date)
  initialSorting: [{ id: "date", desc: true }],

  // Enable all default table features
  enableSorting: true,
  enableGlobalFilter: true,
  enablePagination: false, // Disable for now, can enable later

  // Custom row styling - using date field instead of dateRange since we removed it to match V1
  getRowStyle: row => {
    // Dim medications older than 1 year based on date field
    if (row.date && row.date !== "-") {
      const isOld = dayjs().diff(dayjs(row.date), "years") >= 1;
      if (isOld) {
        return { opacity: 0.7 };
      }
    }
    return undefined;
  },

  // Custom row click handling for Medications
  isRowClickable: (row: MedicationSeriesRowData) => {
    // Medications are clickable if they have originalData
    return Boolean(row.originalData);
  },

  onRowClick: (row, onDataClick, resourceType) => {
    // For Medications, we need to create a series-like structure for the sidepanel
    if (row.originalData) {
      // Type guard to check if it's an EnhancedMedicationSeries
      const isEnhancedSeries = (data: unknown): data is EnhancedMedicationSeries => {
        return Boolean(data && typeof data === "object" && "events" in data);
      };

      // Extract actual FHIR resources from the events for AI Analysis
      const fhirResources = isEnhancedSeries(row.originalData)
        ? row.originalData.events?.map(
            (event: unknown) => (event as Record<string, unknown>).resource
          ) || []
        : [];

      // Get the medication name from the enhanced series if available
      const medicationName = isEnhancedSeries(row.originalData)
        ? row.originalData.codeDisplay
        : row.medication;

      // Create a mock series structure for the sidepanel
      const mockSeries: BaseSeries<unknown> = {
        code: row.code,
        codeDisplay: medicationName,
        resourceType: MEDICATION_RESOURCE_TYPE,
        resources: fhirResources, // Pass actual FHIR resources for AI Analysis
        earliestDate: isEnhancedSeries(row.originalData) ? row.originalData.earliestDate : row.date,
        latestDate: isEnhancedSeries(row.originalData) ? row.originalData.latestDate : row.date,
      };

      // Wrap in SidePanelData format
      const sidePanelData: SidePanelData<unknown> = {
        type: "series",
        series: mockSeries,
      };

      // Use the medication name as the title and pass row data
      onDataClick(sidePanelData, resourceType, medicationName, undefined, row);
    }
  },
};
